import path from 'path-browserify';
import { Mime } from 'mime/lite';
import standardTypes from 'mime/types/standard.js';
import otherTypes from 'mime/types/other.js';

const mime = new Mime(standardTypes, otherTypes);

/**
 * Defines the MIME type for Lottie animations
 */
mime.define({ 'application/lottie': ['lottie'] }, true);

/**
 * The primary MIME types used for classifying assets i.e. video/mp4
 * @readonly
 * @enum {string}
 */
const MIME_PRIMARY_TYPES = Object.freeze({
	IMAGE: 'image',
	VIDEO: 'video',
	MODEL: 'model',
	AUDIO: 'audio',
	TEXT: 'text',
	APPLICATION: 'application', // json, lottie, pdf
});

/**
 * The secondary MIME types used for classifying assets i.e. json, lottie
 * @readonly
 * @enum {string}
 */
const MIME_SECONDARY_TYPES = Object.freeze({
	JSON: 'json',
	LOTTIE: 'lottie',
});

/**
 * Cleans a URL by removing query strings and hash fragments
 * @param {string} src - The URL to clean
 * @returns {string} The cleaned URL
 */
const cleanUrl = (src = '') => {
	const url = new URL(src);
	url.search = '';
	url.hash = '';
	return url.toString();
};

/**
 * Gets the primary MIME type of a resource
 * @param {string} src - The URL of the resource
 * @returns {string} The primary MIME type
 */
export const getMimePrimaryType = (src = '') => {
	const mimeType = mime.getType(src);
	const [primaryType, secondaryType] = mimeType.split('/');
	return primaryType;
};

/**
 * Gets the secondary MIME type of a resource
 * @param {string} src - The URL of the resource
 * @returns {string} The secondary MIME type
 */
export const getMimeSecondaryType = (src = '') => {
	const mimeType = mime.getType(src);
	const [primaryType, secondaryType] = mimeType.split('/');
	return secondaryType;
};

/**
 * Extracts the filename from a URL
 * @param {string} src - The URL
 * @returns {string} The filename
 */
export const getFilename = (src = '') => {
	if (!src) return null;
	const url = cleanUrl(src);
	const { name } = path.parse(url);
	return name;
};

/**
 * Extracts the file extension from a URL
 * @param {string} src - The URL
 * @returns {string} The file extension i.e. .txt
 */
export const getFileExtension = (src = '') => {
	if (!src) return null;
	const url = cleanUrl(src);
	const { ext } = path.parse(url);
	return ext;
};

/**
 * Extracts the filename with extension from a URL
 * @param {string} src - The URL
 * @returns {string} The filename with extension i.e. filename.txt
 */
export const getFilenameWithExtension = (src = '') => {
	if (!src) return null;
	const url = cleanUrl(src);
	const { name, ext } = path.parse(url);
	return `${name}${ext}`;
};

/**
 * Checks if the source is an image asset
 * @param {string} src - The URL of the resource
 * @returns {boolean} True if the source is an image asset
 */
export const checkIsImageAsset = (src = '') => {
	if (!src) return false;
	const url = cleanUrl(src);
	const primaryType = getMimePrimaryType(url);
	return primaryType === MIME_PRIMARY_TYPES.IMAGE;
};

/**
 * Checks if the source is an audio asset
 * @param {string} src - The URL of the resource
 * @returns {boolean} True if the source is an audio asset
 */
export const checkIsAudioAsset = (src = '') => {
	if (!src) return false;
	const url = cleanUrl(src);
	const primaryType = getMimePrimaryType(url);
	return primaryType === MIME_PRIMARY_TYPES.AUDIO;
};

/**
 * Checks if the source is a video asset
 * @param {string} src - The URL of the resource
 * @returns {boolean} True if the source is a video asset
 */
export const checkIsVideoAsset = (src = '') => {
	if (!src) return false;
	const url = cleanUrl(src);
	const primaryType = getMimePrimaryType(url);
	return primaryType === MIME_PRIMARY_TYPES.VIDEO;
};

/**
 * Checks if the source is a 3D model asset
 * @param {string} src - The URL of the resource
 * @returns {boolean} True if the source is a 3D model asset
 */
export const checkIs3DModelAsset = (src = '') => {
	if (!src) return false;
	const url = cleanUrl(src);
	const primaryType = getMimePrimaryType(url);
	return primaryType === MIME_PRIMARY_TYPES.MODEL;
};

/**
 * Checks if the source is a lottie asset
 * @param {string} src - The URL of the resource
 * @returns {boolean} True if the source is a lottie asset
 */
export const checkIsLottieAsset = (src = '') => {
	if (!src) return false;
	const url = cleanUrl(src);
	const primaryType = getMimePrimaryType(url);
	const secondaryType = getMimeSecondaryType(url);
	return (
		primaryType === MIME_PRIMARY_TYPES.APPLICATION &&
		[MIME_SECONDARY_TYPES.JSON, MIME_SECONDARY_TYPES.LOTTIE].includes(
			secondaryType,
		)
	);
};
